import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Users,
    Crown,
    Trash2,
    Loader2,
    Search,
    FolderKanban,
    UserCircle,
    TrendingUp,
    FileText,
    LayoutTemplate,
    UserPlus,
    X,
} from "lucide-react";
import {
    adminListWorkgroups,
    adminDeleteWorkgroup,
    adminUsers,
    adminAddWorkgroupMember,
} from "../../lib/api";

/**
 * iter-119: Admin → Workgroups tab.
 *
 * Superadmin oversight of every workgroup in the system:
 *  - Card per workgroup with name, member list (owner marked with a
 *    crown), and per-collection doc counts.
 *  - Delete button (superadmin only) with double-confirm modal.
 *
 * Reviewer accounts see the same list read-only — the Delete button
 * is disabled + tooltipped with the reason.
 */
export default function WorkgroupsTab({ user }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [search, setSearch] = useState("");
    // iter-153 — pending add-member modal state ({ wg } or null).
    const [addingTo, setAddingTo] = useState(null);

    const isSuperadmin = (user?.role || "") === "superadmin";

    const load = async () => {
        setLoading(true);
        try {
            const wgs = await adminListWorkgroups();
            setItems(wgs || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load workgroups.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const del = async (wg) => {
        const confirmMsg =
            `Delete workgroup "${wg.name}"?\n\n` +
            `• All ${wg.members?.length || 0} member(s) will be kicked out.\n` +
            `• Every shared project, contact, lead, estimate, and template ` +
            `will be un-stamped and returned to its original owner.\n\n` +
            `This action cannot be undone. Type OK in the next prompt to confirm.`;
        if (!window.confirm(confirmMsg)) return;
        const typed = window.prompt(`Type OK to permanently delete "${wg.name}":`);
        if ((typed || "").trim().toUpperCase() !== "OK") {
            toast.message("Delete cancelled.");
            return;
        }
        setBusy(wg.id);
        try {
            await adminDeleteWorkgroup(wg.id);
            toast.success(`"${wg.name}" deleted.`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete workgroup.");
        } finally {
            setBusy("");
        }
    };

    const q = search.trim().toLowerCase();
    const filtered = !q
        ? items
        : items.filter((w) => {
              if ((w.name || "").toLowerCase().includes(q)) return true;
              return (w.members || []).some(
                  (m) =>
                      (m.name || "").toLowerCase().includes(q) ||
                      (m.email || "").toLowerCase().includes(q),
              );
          });

    return (
        <section className="space-y-4" data-testid="admin-workgroups">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-white text-base font-bold flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#00A3FF]" />
                        Workgroups
                        <span className="text-slate-500 font-normal text-xs">
                            ({items.length})
                        </span>
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">
                        Every workgroup in the system, with members and per-collection doc
                        counts. Superadmin can delete any workgroup — the data goes back to
                        its original owners.
                    </p>
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="search"
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="bg-[#0A1128] border border-[#2A3655] rounded-sm pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00A3FF] w-56"
                        data-testid="admin-wg-search"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading workgroups…
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-slate-500 border border-dashed border-[#2A3655] rounded-sm px-4 py-10 text-center">
                    {items.length === 0
                        ? "No workgroups exist yet."
                        : "No workgroups match that search."}
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {filtered.map((wg) => (
                        <WorkgroupCard
                            key={wg.id}
                            wg={wg}
                            busy={busy === wg.id}
                            canDelete={isSuperadmin}
                            canAddMember={isSuperadmin}
                            onDelete={() => del(wg)}
                            onAddMember={() => setAddingTo(wg)}
                        />
                    ))}
                </div>
            )}

            {addingTo && (
                <AddMemberModal
                    wg={addingTo}
                    currentUserId={user?.user_id}
                    onClose={() => setAddingTo(null)}
                    onAdded={() => {
                        setAddingTo(null);
                        load();
                    }}
                />
            )}
        </section>
    );
}

// iter-153 — modal that lists every existing user not already in the
// workgroup so a superadmin can drop them in with one click. Includes
// the superadmin themselves so they can join a customer's workgroup
// for support without going through the invite dance.
function AddMemberModal({ wg, currentUserId, onClose, onAdded }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [addingId, setAddingId] = useState("");
    const [role, setRole] = useState("member");

    useEffect(() => {
        (async () => {
            try {
                const rows = await adminUsers();
                setUsers(rows || []);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't load users.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const memberIds = useMemo(
        () => new Set((wg.members || []).map((m) => m.user_id)),
        [wg],
    );

    const eligible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return users
            .filter((u) => !memberIds.has(u.user_id))
            .filter((u) => {
                if (!q) return true;
                return (
                    (u.email || "").toLowerCase().includes(q)
                    || (u.name || "").toLowerCase().includes(q)
                    || (u.user_id || "").toLowerCase().includes(q)
                );
            })
            .sort((a, b) => (a.email || "").localeCompare(b.email || ""));
    }, [users, memberIds, query]);

    const add = async (u) => {
        setAddingId(u.user_id);
        try {
            await adminAddWorkgroupMember(wg.id, u.user_id, role);
            toast.success(`Added ${u.email || u.name || u.user_id} to "${wg.name}".`);
            onAdded();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't add member.");
        } finally {
            setAddingId("");
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
            data-testid="add-member-modal"
        >
            <div
                className="bg-[#0A1128] border border-[#2A3655] rounded max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-3 p-4 border-b border-[#2A3655]">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00A3FF]">
                            Add member
                        </div>
                        <h3 className="text-white font-bold text-base truncate mt-0.5">
                            {wg.name}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded hover:bg-white/10 text-slate-400"
                        data-testid="add-member-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                <div className="p-3 border-b border-[#2A3655] space-y-2">
                    <div className="relative">
                        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="search"
                            placeholder="Search users by name, email or ID…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="w-full bg-[#0F1B3A] border border-[#2A3655] rounded-sm pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00A3FF]"
                            data-testid="add-member-search"
                            autoFocus
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                            Role
                        </label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            data-testid="add-member-role"
                            className="h-8 bg-[#0F1B3A] border border-[#2A3655] rounded-sm text-white text-xs px-2"
                        >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
                        </div>
                    ) : eligible.length === 0 ? (
                        <div className="p-8 text-center text-sm text-slate-500">
                            {query.trim()
                                ? "No matching users outside this workgroup."
                                : "Every user is already a member."}
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#2A3655]/60">
                            {eligible.map((u) => {
                                const isMe = u.user_id === currentUserId;
                                return (
                                    <li
                                        key={u.user_id}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5"
                                        data-testid={`add-member-row-${u.user_id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-white truncate flex items-center gap-1.5">
                                                {u.name || u.email || u.user_id}
                                                {isMe && (
                                                    <span className="text-[9px] uppercase tracking-wider bg-[#00A3FF]/20 text-[#00A3FF] px-1.5 py-0.5 rounded">
                                                        you
                                                    </span>
                                                )}
                                                {u.role === "superadmin" && (
                                                    <Crown className="w-3 h-3 text-amber-400" />
                                                )}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {u.email || u.user_id}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => add(u)}
                                            disabled={!!addingId}
                                            data-testid={`add-member-btn-${u.user_id}`}
                                            className="shrink-0 inline-flex items-center gap-1 h-7 px-3 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-[#007AFF] hover:bg-[#0369A1] text-white disabled:opacity-40"
                                        >
                                            {addingId === u.user_id ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                                <UserPlus className="w-3 h-3" />
                                            )}
                                            Add
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function WorkgroupCard({ wg, busy, canDelete, canAddMember, onDelete, onAddMember }) {
    const owner = (wg.members || []).find(
        (m) => m.user_id === wg.created_by_user_id,
    );
    const counts = wg.counts || {};
    const tiles = [
        { label: "Projects", n: counts.projects || 0, icon: FolderKanban },
        { label: "Contacts", n: counts.contacts || 0, icon: UserCircle },
        { label: "Leads", n: counts.leads || 0, icon: TrendingUp },
        { label: "Estimates", n: counts.estimates || 0, icon: FileText },
        { label: "Templates", n: counts.templates || 0, icon: LayoutTemplate },
    ];
    return (
        <div
            className="rounded-sm border border-[#2A3655] bg-[#0A1128] overflow-hidden"
            data-testid={`admin-wg-${wg.id}`}
        >
            <header className="flex items-start justify-between gap-2 px-4 py-3 border-b border-[#2A3655] bg-[#0F1B3A]/60">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-white truncate">{wg.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                        Owner: {owner?.name || owner?.email || "—"}
                        <span className="mx-2 text-slate-700">·</span>
                        Created {wg.created_at ? new Date(wg.created_at).toLocaleDateString() : "—"}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                <button
                    type="button"
                    onClick={onAddMember}
                    disabled={!canAddMember || busy}
                    title={!canAddMember ? "Superadmin only" : "Add a user to this workgroup"}
                    data-testid={`admin-wg-add-member-${wg.id}`}
                    className="shrink-0 inline-flex items-center gap-1 h-8 px-3 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-[#00A3FF]/40 text-[#00A3FF] hover:bg-[#00A3FF]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <UserPlus className="w-3 h-3" />
                    Add user
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    disabled={!canDelete || busy}
                    title={!canDelete ? "Superadmin only" : "Delete workgroup"}
                    data-testid={`admin-wg-delete-${wg.id}`}
                    className="shrink-0 inline-flex items-center gap-1 h-8 px-3 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {busy ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <Trash2 className="w-3 h-3" />
                    )}
                    Delete
                </button>
                </div>
            </header>

            {/* Doc counts */}
            <div className="grid grid-cols-5 gap-px bg-[#2A3655]">
                {tiles.map(({ label, n, icon: Icon }) => (
                    <div key={label} className="bg-[#0A1128] px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                            <Icon className="w-3 h-3 text-[#00A3FF]" />
                        </div>
                        <div className="text-sm font-bold text-white mt-0.5">{n}</div>
                        <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5">
                            {label}
                        </div>
                    </div>
                ))}
            </div>

            {/* Members */}
            <div className="px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                    Members ({wg.members?.length || 0})
                </div>
                <ul className="space-y-1.5">
                    {(wg.members || []).map((m) => (
                        <li
                            key={m.id}
                            className="flex items-center gap-2 text-xs"
                            data-testid={`admin-wg-member-${m.user_id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-white truncate flex items-center gap-1.5">
                                    {m.name || m.email || m.user_id}
                                    {m.user_id === wg.created_by_user_id && (
                                        <Crown className="w-3 h-3 text-amber-400" />
                                    )}
                                </div>
                                <div className="text-[10px] text-slate-500 truncate">
                                    {m.email}
                                </div>
                            </div>
                            <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold shrink-0">
                                {m.role}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
